// Consultation/ConsultationItem.tsx
import React, {useContext, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import ConsultationActions from "Consultation/ConsultationActions";
import ConsultationStatus from "Consultation/ConsultationStatus";
import {useProfile} from "User/ProfileContext";
import {useTheme} from "Theme/ThemeContext";
import UserAvatarGroup from "User/UserAvatarGroup";
import {FCCC, FR, FRB, FRBC, FRSC} from 'WideLayout/Layouts';
import CircularProgress from 'Core/components/elements/CircularProgress';
import {IConsultation} from "../../Types/psychology/consultation";
import pprint from "../../Utils/pprint";
import {AuthContext, AuthContextType} from "Auth/AuthContext";


interface ConsultationItemProps {
    consultation: IConsultation;
    actionsEnable?: boolean;
    className?: string;
    extended?: boolean;
    onConsultationCancel?: () => void;
    onBalanceChange?: () => void;
}

const ConsultationItem: React.FC<ConsultationItemProps> = (
    {
        consultation: initialConsultation,
        actionsEnable = true,
        className,
        extended = false,
        onConsultationCancel = () => {
        },
        onBalanceChange = () => {
        },
    }) => {
    const {isAuthenticated, user} = useContext(AuthContext) as AuthContextType;
    const {selectedProfile} = useProfile();
    const {plt} = useTheme();
    const navigate = useNavigate();
    const [consultation, setConsultation] = useState<IConsultation>(initialConsultation);
    const [isLoading, setIsLoading] = useState<boolean>(false); // Стейт загрузки

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const options: Intl.DateTimeFormatOptions = {month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'};
        if (date.getFullYear() === now.getFullYear()) return date.toLocaleString('ru-RU', options);
        else return date.toLocaleString('ru-RU', {...options, year: 'numeric'});
    };


    const handleConsultationUpdate = (updatedConsultation: IConsultation) => {
        pprint('Consultation update:', updatedConsultation);
        setConsultation(updatedConsultation);
        onBalanceChange(); // Триггер обновления баланса
    };

    if (!consultation.config) return <FCCC><CircularProgress size={'80px'}/></FCCC>;

    return (
        <FRBC wrap g={1} p={2}
              rounded={3} pos={'relative'} bg={plt.colors.secondary.lighter}
              cls={`${className} consultation hover-scale-1 trans-100-eio `}
              boxShadow={`0.06rem 0.06rem 0.37rem -0.1rem ${plt.bg.contrast20}`}>
            <FRBC wrap g={1} onClick={() => navigate(`/consultations/${consultation.id}`)}>
                <FRSC g={1} position={'relative'} sx={{color: plt.text.primary80}}>
                    {selectedProfile === 'psychologist'
                        ? <UserAvatarGroup users={consultation.clients}/>
                        : <UserAvatarGroup users={consultation.psychologists}/>}
                </FRSC>
                <FR w={'min-content'} fontSize={'.7rem'}
                    color={plt.text.primary25}
                    pos={'absolute'} top={1} right={5}>
                    ID:{consultation.id}
                </FR>
                <FRB g={1} wrap pl={2}>
                    <FRB g={1} wrap sx={{color: plt.text.primary60}}>
                        {consultation.date &&
                            <span className={'fw-3 text-nowrap ps-2'}>{formatDate(consultation.date)}</span>
                        }
                        {consultation.config.duration && (
                            <span className={'fw-3 consultation-category pe-2'}>
                                {consultation.config.duration.value} минут
                            </span>)
                        }
                    </FRB>
                    {user && <ConsultationStatus
                        consultation={consultation}
                    />}
                </FRB>
            </FRBC>
            {actionsEnable && <ConsultationActions
                cls={'ms-auto'}
                consultation={consultation}
                extended={extended}
                onConsultationCancel={handleConsultationUpdate}
                onConsultationReschedule={handleConsultationUpdate}
                onConsultationUpdate={handleConsultationUpdate}
                setLoading={setIsLoading}
            />}
            {isLoading && (
                <FCCC pos={'absolute'} className={'backdrop-blur-10'}
                      top={0} left={0} zIndex={8} rounded={3}
                      w={'100%'} h={'100%'}>
                    <CircularProgress size={'3rem'}/>
                </FCCC>
            )}
        </FRBC>
    );
};

export default ConsultationItem;
